// Active alarm extraction from the device realtime status `general` node.
//
// This mirrors the business rules used by the Inobram App
// (lib/app/modules/dashboard/models/dashboard_smaai5_alarms_model.dart and
// lib/app/modules/home/modules/alarm/interactor/controllers/alarm_controller.dart):
//
// - SMAAI5 family devices expose one numeric flag per alarm in the `general`
//   node. The alarm is active when its value is greater than zero.
// - InobramX (base) devices expose the active alarm codes as a JSON array
//   string in `general.a_a_c`, which are translated by alarm code.

// Maps the SMAAI5 `general` node alarm flags to the existing i18n alarm keys
// (see `alarm` section in src/i18n/index.js).
// `code` is the alarm code the SMAAI5 firmware publishes for the same alarm in
// its push notifications (shared alarm_translators codes), which lets an active
// alarm be matched with the notification it generated.
export const SMAAI5_ALARM_DICTIONARY = [
  { key: 'alarm_disarm', i18nKey: 'DESARME_CORT', code: 75 },
  { key: 'alarm_high_co2', i18nKey: 'high_co2', code: 5 },
  { key: 'alarm_high_humidity', i18nKey: 'high_humidity', code: 3 },
  { key: 'alarm_high_pressure', i18nKey: 'high_pressure', code: 6 },
  { key: 'alarm_high_temperature', i18nKey: 'high_temperature', code: 1 },
  { key: 'alarm_low_battery', i18nKey: 'low_battery', code: 7 },
  { key: 'alarm_low_humidity', i18nKey: 'low_humidity', code: 4 },
  { key: 'alarm_low_pressure', i18nKey: 'low_pressure', code: 132 },
  { key: 'alarm_low_source', i18nKey: 'low_source', code: 8 },
  { key: 'alarm_low_temperature', i18nKey: 'low_temperature', code: 2 },
  { key: 'alarm_no_probe_installed', i18nKey: 'no_probe_installed', code: 133 },
  { key: 'alarm_null_temperature', i18nKey: 'null_temperature', code: 134 },
  { key: 'alarm_relay_error', i18nKey: 'relay_error', code: 136 }
]

function parseInobramxCodes (value) {
  if (Array.isArray(value)) {
    return value
  }
  if (typeof value === 'string' && value.length > 0) {
    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    } catch (err) {
      return []
    }
  }
  return []
}

function findTranslation (translations, code) {
  if (!Array.isArray(translations)) {
    return null
  }
  return translations.find(translation => translation.code === code) || null
}

// Resolves the barn description for a device, falling back to its own
// description and finally the MAC address.
function barnDescription (device) {
  if (device.barn && device.barn.description) {
    return device.barn.description
  }
  return device.description || device.mac || ''
}

// Resolves a human description that may be exposed directly (`description`/
// `name`, v1 shape) or nested in a `details` array (`details[0].description`,
// the v2 barns/nuclei/properties shape).
function resolveDescription (entity) {
  if (!entity) {
    return ''
  }
  if (entity.description || entity.name) {
    return entity.description || entity.name
  }
  if (Array.isArray(entity.details) && entity.details.length) {
    const first = entity.details[0]
    return (first && (first.description || first.name)) || ''
  }
  return ''
}

// Builds the list of active alarms for a single device from its `general`
// realtime node. Each returned item is a plain object ready to be displayed.
export function extractActiveAlarms (general, device, translations) {
  if (!general || !device) {
    return []
  }

  const barn = device.barn || {}
  const nucleus = barn.nucleus || {}
  const property = nucleus.property || {}

  const baseItem = {
    deviceId: device.id,
    mac: device.mac,
    barnId: barn.id || null,
    deviceDescription: barnDescription(device),
    barnDescription: resolveDescription(barn) || device.description || device.mac || '',
    nucleusDescription: resolveDescription(nucleus),
    propertyDescription: resolveDescription(property),
    recAt: general.rec_at || null,
    age: general.age != null ? general.age : null
  }

  // InobramX (base) devices: active alarm codes in `general.a_a_c`.
  if (device.base === 'inobramx') {
    const codes = parseInobramxCodes(general.a_a_c)
    return codes.map(code => {
      const translation = findTranslation(translations, code)
      return Object.assign({}, baseItem, {
        alarmKey: null,
        i18nKey: null,
        code: code,
        description: translation ? translation.description : null,
        count: 1
      })
    })
  }

  // SMAAI5 family devices: numeric alarm flags in `general`.
  return SMAAI5_ALARM_DICTIONARY
    .filter(entry => (general[entry.key] || 0) > 0)
    .map(entry => Object.assign({}, baseItem, {
      alarmKey: entry.key,
      i18nKey: entry.i18nKey,
      code: entry.code,
      description: null,
      count: general[entry.key]
    }))
}

// Returns true when the device model/base is supported by the alarms dashboard
// (same set used by the charts dashboard).
export function isAlarmSupportedDevice (device) {
  if (!device) {
    return false
  }
  if (device.base === 'inobramx') {
    return true
  }
  return ['smaai5', 'smaai5pro', 'smaai5smart', 'n-pro'].includes(device.type)
}



// WEBPACK FOOTER //
// ./src/utils/active-alarms.js